import * as React from 'react';
import manageAlarmsStyle from '../ManageAlarmsStyle';
import {
    Box,
    Checkbox,
    Divider,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    TextField,
} from '@mui/material';
import * as PXBColors from '@brightlayer-ui/colors';
import { useLanguageLocale } from '../../../hooks/language-locale-hooks';
import { AlarmConfigData, ManageDigitalAlarmConfig, alarmOn } from '../../../types';
import { DECIMAL_NUMBER, RESTRICT_SPEC_WITHOUT_SPACE_REGEX } from '../../../constants';

export const ConfigureDigitalAlarms: React.FC<{
    index: number;
    value: number;
    alarmConfigData: AlarmConfigData | null;
    updateDigitalAlarmData: Function;
}> = (props) => {
    const { t } = useLanguageLocale();
    const classes = manageAlarmsStyle();

    const [alarmConfig, setAlarmConfig] = React.useState<ManageDigitalAlarmConfig | null>(null);

    const { value, index, ...other } = props;

    const onChangeValues = (val: number | string | boolean, name: string): void => {
        if (alarmConfig) {
            const alarmConfigObj: any = { ...alarmConfig };
            alarmConfigObj[`${name}`] = val;
            setAlarmConfig(alarmConfigObj);
            props.updateDigitalAlarmData(alarmConfigObj);
        }
    };

    const onTextChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>, name: string): void => {
        if (RESTRICT_SPEC_WITHOUT_SPACE_REGEX.test(e.target.value)) {
            onChangeValues(e.target.value, name);
        }
    };

    const onNumChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>, name: string): void => {
        const numValue = e.target.value;
        if (new RegExp(DECIMAL_NUMBER).test(numValue) || numValue === '') {
            onChangeValues(e.target.value, name);
        }
    };

    const onCheckChange = (val: boolean, name: string): void => {
        onChangeValues(val, name);
    };

    const handleCheckChange = (event: any, name: string): void => {
        onCheckChange(event.target.checked, name);
    };

    const handleAlarmChange = (_value: string | boolean, name: string): void => {
        onChangeValues(_value, name);
    };

    const radioBtnChange = (event: any): void => {
        onChangeValues(event.target.value, 'alarmType');
    };

    React.useEffect(() => {
        if (props.alarmConfigData) {
            if (props.alarmConfigData.alarmConfigurations.length === 0) {
                const defaultValue: ManageDigitalAlarmConfig = {
                    alarmEnabled: false,
                    alarmMessage: '',
                    alarmType: '',
                    alarmOnValue: false,
                    priority: 9999,
                    ackHoldOff: {
                        checked: false,
                        time: 0
                    },
                    rearm: {
                        checked: false,
                        time: 0
                    },
                    alarmDelay: {
                        checked: false,
                        time: 0
                    },
                    latching: false
                };
                setAlarmConfig(defaultValue);
            } else setAlarmConfig(props.alarmConfigData?.alarmConfigurations[0].alarmConfiguration);
        }
    }, []);

    return (
        <>
            {alarmConfig && (
                <div
                    role="tabpanel"
                    hidden={value !== index}
                    id={`simple-tabpanel-${index}`}
                    aria-labelledby={`simple-tab-${index}`}
                    {...other}
                    style={{ maxHeight: '62vh', minHeight: '62vh', overflowX: 'hidden', overflowY: 'auto' }}
                >
                    <>
                        <Box className={classes.paddingTextbox}>
                            <TextField
                                inputProps={{ 'data-testid': 'alarm-name', maxLength: 5 }}
                                fullWidth
                                required
                                variant="filled"
                                id="AlarmPriority"
                                label="Alarm Prioirty"
                                value={alarmConfig.priority}
                                onChange={(e): void => {
                                    onNumChange(e, 'priority');
                                }}
                            />
                        </Box>
                        <Divider></Divider>
                        <Box sx={{ background: PXBColors.blue[50], color: 'black' }} className={classes.marginpadding8}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        sx={{ color: PXBColors.blue[500] }}
                                        checked={alarmConfig.alarmEnabled}
                                        data-testid="gi-critical"
                                        onChange={(evt): void => handleCheckChange(evt, 'alarmEnabled')}
                                        value={alarmConfig.alarmEnabled}
                                    />
                                }
                                label={t('MANAGEALARMS.ALARM_ENABLE')}
                            />
                        </Box>

                        <Box style={{ width: '98%', paddingLeft: 0 }} className={classes.marginpadding8}>
                            <FormControl variant="filled" required style={{ width: '100%' }}>
                                <InputLabel id="alarmOn" data-testid="alarm-on">
                                    Alarm On
                                </InputLabel>
                                <Select
                                    label="Alarm On"
                                    value={alarmConfig.alarmOnValue}
                                    required
                                    inputProps={{ 'data-testid': 'alarm-on-dd' }}
                                    onChange={(e): void => {
                                        handleAlarmChange(e.target.value, 'alarmOnValue');
                                    }}
                                >
                                    {alarmOn.map((data: { key: string; value: string }, _index: number) => (
                                        <MenuItem key={_index} value={data.key} data-testid={`alarm-on${_index}`}>
                                            {data.value}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                        <Divider></Divider>
                        <Box sx={{ paddingLeft: 0 }}>
                            <FormControl>
                                <RadioGroup
                                    aria-labelledby="demo-controlled-radio-buttons-group"
                                    name="controlled-radio-buttons-group"
                                    value={alarmConfig.alarmType}
                                    onChange={radioBtnChange}
                                >
                                    <Box
                                        sx={{
                                            background: PXBColors.red[50],
                                            width: '432px',
                                        }}
                                        className={classes.marginpadding8}
                                    >
                                        <FormControlLabel
                                            value="critical"
                                            control={<Radio style={{ margin: '6px 4px 4px 10px', padding: '0px' }} />}
                                            label={'Critical'}
                                        />
                                    </Box>

                                    <Box
                                        sx={{
                                            background: PXBColors.yellow[50],

                                            width: '432px',
                                        }}
                                        className={classes.marginpadding8}
                                    >
                                        <FormControlLabel
                                            value="caution"
                                            control={<Radio style={{ margin: '6px 4px 4px 10px', padding: '0px' }} />}
                                            label={'Caution'}
                                        />
                                    </Box>
                                </RadioGroup>
                            </FormControl>
                        </Box>
                        {alarmConfig.alarmType && (
                            <>
                                <Divider></Divider>
                                <Box className={classes.paddingTextbox}>
                                    <TextField
                                        inputProps={{ 'data-testid': 'message' }}
                                        fullWidth
                                        variant="filled"
                                        id="Message"
                                        label="Message"
                                        value={alarmConfig.alarmMessage}
                                        onChange={(e): void => {
                                            onTextChange(e, 'alarmMessage');
                                        }}
                                    />
                                </Box>
                            </>
                        )}
                    </>
                </div>
            )}
        </>
    );
};
